import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'sonner';
import { TenantProvider } from './contexts/TenantContext.tsx';
import { AuthProvider } from './contexts/AuthContext';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { CustomScriptsInjector } from './components/CustomScriptsInjector';
import "./index.css";
import { useTranslation } from 'react-i18next';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
      // Prevent duplicate requests by ensuring proper deduplication
      networkMode: 'online',
    },
  },
});

// Auth Pages
import ProviderLogin from './pages/auth/provider/login';
import ProviderVerify from './pages/auth/provider/verify';
import ProviderForgotPassword from './pages/auth/provider/forgot-password';
import ProviderResetPassword from './pages/auth/provider/reset-password';
import OAuthCallback from './pages/auth/oauth/callback';
import ResetPasswordRedirect from './pages/auth/reset-password-redirect';
import CustomerRegister from './pages/customer/register';
import CheckoutSuccessPage from './pages/customer/checkout-success';
import CustomerInvitationAcceptPage from './pages/customer/invitation/accept';
import TeamInvitationAcceptPage from './pages/team/invitations/accept';
import VerifyEmailPage from './pages/VerifyEmailPage';

// Placeholder for testing
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { ThemeToggle } from "./components/theme-toggle";
import { LanguageSwitcher } from "./components/language-switcher";
import TestTenantPage from './pages/test-tenant';
import { providerRoutes } from './routes/provider';
import { customerRoutes } from './routes/customer';
import { useCustomerAuth } from './contexts/CustomerAuthContext';
import { useProvider } from './hooks/useTenant';

const DEFAULT_REGISTER_PARAMS = {
  productId: '7f28a143-05f2-43a7-8ba3-e20c5dac6de4',
  planId: '019b73aa-7ef8-73d8-98c1-75ddd0e3dcc8',
  cycleId: '019b73aa-7edc-70eb-9b01-7fee8bfd3d50',
  priceId: '019b73aa-7f09-7170-8de9-cc98446e3751',
} as const;

function HomePage() {
  const provider = useProvider();
  const navigate = useNavigate();
  const { isAuthenticated: isCustomerAuthenticated, isLoading: customerAuthLoading } = useCustomerAuth();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background text-foreground p-8">
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>
      <div className="max-w-4xl mx-auto">
        <header className="text-center mb-12 space-y-3">
          <div className="text-3xl font-semibold tracking-tight">{provider?.name ?? t('home.defaultPortalName')}</div>
          <p className="text-sm text-muted-foreground">{t('home.heroSubtitle')}</p>
        </header>

        <div className="grid gap-4 md:grid-cols-2">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/provider/login')}>
            <CardHeader>
              <CardTitle>{t('home.providerCardTitle')}</CardTitle>
              <CardDescription>
                {t('home.providerCardDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {t('home.providerCardCta')}
              </p>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() =>
              navigate(isCustomerAuthenticated ? '/customer' : '/customer/login')
            }
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                navigate(isCustomerAuthenticated ? '/customer' : '/customer/login');
              }
            }}
            aria-busy={customerAuthLoading}
          >
            <CardHeader>
              <CardTitle>{t('home.customerCardTitle')}</CardTitle>
              <CardDescription>
                {t('home.customerCardDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {isCustomerAuthenticated
                  ? t('home.customerCardCtaAuthenticated')
                  : t('home.customerCardCtaAnonymous')}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function DocumentTitleManager() {
  const provider = useProvider();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const providerName = provider?.name?.trim() || t('home.defaultPortalName', 'Portal');
    document.title = providerName;
  }, [provider?.name, i18n.language, t]);

  return null;
}

function CustomerRegisterRoute() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  let needsRedirect = false;

  Object.entries(DEFAULT_REGISTER_PARAMS).forEach(([key, value]) => {
    if (!searchParams.get(key)) {
      searchParams.set(key, value);
      needsRedirect = true;
    }
  });

  if (needsRedirect) {
    return <Navigate to={`/customer/register?${searchParams.toString()}`} replace />;
  }

  return <CustomerRegister />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TenantProvider>
        <DocumentTitleManager />
        <CustomScriptsInjector />
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <CustomerAuthProvider>
              <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/test-tenant" element={<TestTenantPage />} />
              <Route path="/customer/invitation/accept" element={<CustomerInvitationAcceptPage />} />
              <Route path="/team/invitations/accept" element={<TeamInvitationAcceptPage />} />
              <Route path="/verify-email/:id/:hash" element={<VerifyEmailPage />} />
              
              {/* Provider Auth Routes */}
              <Route path="/provider/login" element={<ProviderLogin />} />
              <Route path="/provider/login/verify" element={<ProviderVerify />} />
              <Route path="/provider/forgot-password" element={<ProviderForgotPassword />} />
              <Route path="/provider/reset-password" element={<ProviderResetPassword />} />
              <Route path="/provider/register" element={<Navigate to="/provider/login" replace />} />
              <Route path="/auth/oauth/callback" element={<OAuthCallback />} />
              <Route path="/reset-password" element={<ResetPasswordRedirect />} />
              <Route path="/customer/register" element={<CustomerRegisterRoute />} />
              <Route path="/customer/register/success" element={<CheckoutSuccessPage />} />

              {/* Legacy auth redirects */}
              <Route path="/login" element={<Navigate to="/provider/login" replace />} />
              <Route path="/login/verify" element={<Navigate to="/provider/login/verify" replace />} />

              {/* Provider Portal */}
              {providerRoutes}

              {/* Customer Portal */}
              {customerRoutes}
              
              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </CustomerAuthProvider>
          </AuthProvider>
        </Router>
        <ReactQueryDevtools initialIsOpen={false} />
        <Toaster />
      </TenantProvider>
    </QueryClientProvider>
  );
}

export default App;
